import { createFileRoute, useSearch, useNavigate } from "@tanstack/react-router";
import { useEffect, useContext, useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardContext } from "@/components/dashboard-layout";
import { CommonContactDetails } from "./-components/common-contact-details";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ServiceContactDetails } from "./-components/service-contact-details";
import { Filter } from "./-components/contact-list-toolbar";
import { CommonContactTable } from "./-components/common-contact-table";
import { ServiceContactTable } from "./-components/service-contact-table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ResponsiveFormSection } from "@/components/form/responsive-form";

import {
  createCommonContactFn,
  updateCommonContactFn,
  createServiceContactFn,
  updateServiceContactFn,
  commonContactsQueryOptions,
  serviceContactsQueryOptions,
  deleteCommonContactFn,
  deleteServiceContactFn,
} from "@/server/organisations";
import { PlusIcon } from "lucide-react";
import { useRightSidebarClose } from "@/hooks/use-right-sidebar-close";
import { useRightSidebarOpen } from "@/hooks/use-right-sidebar-open";
import { CrudEntry } from "@/lib/mma/types.gen";
import { checkIfUserMayWrite, checkIfUserMayUpdate, checkIfUserMayDelete, ContextUser } from "@/lib/utils";
import { usePermissionRedirect } from "@/hooks/use-permission-redirect";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/contact-list")({
  component: RouteComponent,
  loader: async ({ params, context }) => {
    return {
      orgId: params.orgId,
      crumb: "Contact list",
      user: (context as unknown as { user?: ContextUser }).user,
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
    };
  },
});

function RouteComponent() {
  const { orgId } = Route.useParams();
  const navigate = useNavigate();
  const context = useContext(DashboardContext);
  const queryClient = useQueryClient();
  const closeRightSidebar = useRightSidebarClose();
  const openRightSidebar = useRightSidebarOpen();
  const commonContactsQuery = useQuery(commonContactsQueryOptions(Number(orgId)));
  const serviceContactsQuery = useQuery(serviceContactsQueryOptions(Number(orgId)));
  const commonContacts = commonContactsQuery.data;
  const serviceContacts = serviceContactsQuery.data;
  const searchParams = useSearch({ strict: false }) as {
    search?: string;
    page?: string;
    perPage?: string;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    commonContactId?: string;
    serviceContactId?: string;
  };
  const { user, userProfileId, accessControl } = Route.useLoaderData();
  usePermissionRedirect({ user, permission: "CONTACT_LIST", redirectTo: "/organisations" });
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "ORGANISATION");
  const mayUpdate = checkIfUserMayUpdate(userProfileId, accessControl, "ORGANISATION");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "ORGANISATION");

  const closeAfterSave = useCallback(
    (contactIdType: "commonContactId" | "serviceContactId") => {
      closeRightSidebar(contactIdType);
    },
    [closeRightSidebar]
  );

  useEffect(() => {
    return () => {
      closeRightSidebar();
    };
  }, [closeRightSidebar]);

  const createCommonContactMutation = useMutation({
    mutationFn: createCommonContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commonContacts"] });
      closeAfterSave("commonContactId");
    },
  });

  const updateCommonContactMutation = useMutation({
    mutationFn: updateCommonContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commonContacts"] });
      closeAfterSave("commonContactId");
    },
  });

  const createServiceContactMutation = useMutation({
    mutationFn: createServiceContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serviceContacts"] });
      closeAfterSave("serviceContactId");
    },
  });

  const updateServiceContactMutation = useMutation({
    mutationFn: updateServiceContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serviceContacts"] });
      closeAfterSave("serviceContactId");
    },
  });

  const deleteCommonContactMutation = useMutation({
    mutationFn: deleteCommonContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commonContacts"] });
      closeAfterSave("commonContactId");
    },
  });

  const deleteServiceContactMutation = useMutation({
    mutationFn: deleteServiceContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serviceContacts"] });
      closeAfterSave("serviceContactId");
    },
  });

  const search = searchParams.search ?? "";

  const handleFilterChange = useCallback(
    (filters: { search?: string }) => {
      navigate({
        to: "/organisations/$orgId/contact-list",
        params: { orgId },
        replace: true,
        search: (prev) => ({
          ...prev,
          search: filters.search?.trim() || undefined,
        }),
      });
    },
    [navigate, orgId]
  );

  const handleAddCommonContact = useCallback(() => {
    if (!context) {
      return;
    }
    navigate({
      to: "/organisations/$orgId/contact-list",
      params: { orgId },
      replace: true,
      search: (prev) => ({ ...prev, commonContactId: "new", serviceContactId: undefined }),
    });
  }, [context, navigate, orgId]);

  const handleAddServiceContact = useCallback(() => {
    if (!context) {
      return;
    }
    navigate({
      to: "/organisations/$orgId/contact-list",
      params: { orgId },
      replace: true,
      search: (prev) => ({ ...prev, commonContactId: undefined, serviceContactId: "new" }),
    });
  }, [context, navigate, orgId]);

  const saveCommonContact = useCallback(
    async (data: any) => {
      const isNewContact = !data.id;
      if (isNewContact) {
        try {
          const contactPromise = createCommonContactMutation.mutateAsync({
            data: {
              id: orgId,
              payload: data,
            },
          });
          toast.promise(contactPromise, {
            loading: "Creating common contact...",
            success: () => {
              return "Common contact created";
            },
            error: (error) => error.message || "Failed to create common contact",
          });
          return await contactPromise;
        } catch (error) {
          console.error(error);
          throw error;
        }
      } else {
        try {
          const contactPromise = updateCommonContactMutation.mutateAsync({ data: { orgId, payload: data } });
          toast.promise(contactPromise, {
            loading: "Updating common contact...",
            success: () => {
              return "Common contact updated";
            },
            error: (error) => error.message || "Failed to update common contact",
          });
          return await contactPromise;
        } catch (error) {
          console.error(error);
          throw error;
        }
      }
    },
    [orgId, createCommonContactMutation.mutateAsync, updateCommonContactMutation.mutateAsync]
  );

  const saveServiceContact = useCallback(
    async (values: any) => {
      const payload = {
        ...values,
        phonebookIndex: values.phonebookIndex ? Number(values.phonebookIndex) : undefined,
      };

      const isNewContact = !payload.id;
      if (isNewContact) {
        try {
          const contactPromise = createServiceContactMutation.mutateAsync({ data: { orgId, payload } });
          toast.promise(contactPromise, {
            loading: "Creating service contact...",
            success: () => {
              return "Service contact created";
            },
            error: (error) => error.message || "Failed to create service contact",
          });
          return await contactPromise;
        } catch (error) {
          console.error(error);
          throw error;
        }
      } else {
        try {
          const contactPromise = updateServiceContactMutation.mutateAsync({ data: { orgId, payload } });
          toast.promise(contactPromise, {
            loading: "Updating service contact...",
            success: () => {
              return "Service contact updated";
            },
            error: (error) => error.message || "Failed to update service contact",
          });
          return await contactPromise;
        } catch (error) {
          console.error(error);
          throw error;
        }
      }
    },
    [orgId, createServiceContactMutation.mutateAsync, updateServiceContactMutation.mutateAsync]
  );

  const deleteCommonContact = useCallback(
    async (contactId: number) => {
      try {
        const contactPromise = deleteCommonContactMutation.mutateAsync({
          data: { orgId, contactId: contactId.toString() },
        });
        toast.promise(contactPromise, {
          loading: "Deleting common contact...",
          success: () => {
            return "Common contact deleted";
          },
          error: (error) => error.message || "Failed to delete common contact",
        });
        return await contactPromise;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [orgId, deleteCommonContactMutation.mutateAsync]
  );

  const deleteServiceContact = useCallback(
    async (contactId: number) => {
      try {
        const contactPromise = deleteServiceContactMutation.mutateAsync({
          data: { orgId, contactId: contactId.toString() },
        });
        toast.promise(contactPromise, {
          loading: "Deleting service contact...",
          success: () => {
            return "Service contact deleted";
          },
          error: (error) => error.message || "Failed to delete service contact",
        });
        return await contactPromise;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [orgId, deleteServiceContactMutation.mutateAsync]
  );

  const sidebarComponent = useMemo(() => {
    if (searchParams.commonContactId !== undefined && searchParams.commonContactId !== null) {
      if (searchParams.commonContactId === "new") {
        return (
          <CommonContactDetails
            orgId={Number(orgId)}
            saveCommonContact={saveCommonContact}
            deleteContact={() => { }}
            close={() => closeAfterSave("commonContactId")}
            mayWrite={mayWrite}
          />
        );
      } else {
        return (
          <CommonContactDetails
            contactId={Number(searchParams.commonContactId)}
            orgId={Number(orgId)}
            saveCommonContact={saveCommonContact}
            deleteContact={deleteCommonContact}
            close={() => closeAfterSave("commonContactId")}
            mayWrite={mayWrite}
            mayUpdate={mayUpdate}
            mayDelete={mayDelete}
          />
        );
      }
    } else if (searchParams.serviceContactId !== undefined && searchParams.serviceContactId !== null) {
      if (searchParams.serviceContactId === "new") {
        return (
          <ServiceContactDetails
            orgId={Number(orgId)}
            saveServiceContact={saveServiceContact}
            deleteContact={() => { }}
            close={() => closeAfterSave("serviceContactId")}
            mayWrite={mayWrite}
          />
        );
      } else {
        return (
          <ServiceContactDetails
            contactId={Number(searchParams.serviceContactId)}
            orgId={Number(orgId)}
            saveServiceContact={saveServiceContact}
            deleteContact={deleteServiceContact}
            close={() => closeAfterSave("serviceContactId")}
            mayWrite={mayWrite}
            mayUpdate={mayUpdate}
            mayDelete={mayDelete}
          />
        );
      }
    }
    return null;
  }, [
    searchParams.commonContactId,
    searchParams.serviceContactId,
    orgId,
    saveCommonContact,
    saveServiceContact,
    deleteCommonContact,
    deleteServiceContact,
    mayWrite,
    mayUpdate,
    mayDelete,
    closeAfterSave,
  ]);

  const paramKeys = useMemo(() => {
    if (searchParams.commonContactId !== undefined) return "commonContactId";
    if (searchParams.serviceContactId !== undefined) return "serviceContactId";
    return undefined;
  }, [searchParams.commonContactId, searchParams.serviceContactId]);

  // Handle sidebar opening/closing based on search params
  useEffect(() => {
    if (!context) {
      return;
    }

    const isNewCommon = searchParams.commonContactId === "new";
    const isNewService = searchParams.serviceContactId === "new";
    const isNew = isNewCommon || isNewService;
    const hasPermission = !isNew || mayWrite;

    // If no paramKeys, close the sidebar
    if (!paramKeys) {
      closeRightSidebar();
      return;
    }

    openRightSidebar({
      component: sidebarComponent,
      paramKeys: paramKeys,
      shouldClose: false,
      permissionCheck: !hasPermission,
      onPermissionDenied: () => {
        if (isNewCommon) {
          closeAfterSave("commonContactId");
        } else if (isNewService) {
          closeAfterSave("serviceContactId");
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sidebarComponent,
    paramKeys,
    mayWrite,
    searchParams.commonContactId,
    searchParams.serviceContactId,
    openRightSidebar,
    closeAfterSave,
    closeRightSidebar,
  ]);

  if (!context) {
    return (
      <ResponsiveFormSection className="flex flex-col gap-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-44 w-full" />
      </ResponsiveFormSection>
    );
  }

  return (
    <ResponsiveFormSection className="flex flex-col gap-4">
      <Filter onFilterChange={handleFilterChange} defaultSearch={search} />
      <Card>
        <CardHeader className="flex justify-between min-h-\[36px\]">
          <CardTitle>Common contacts</CardTitle>
          {searchParams.commonContactId !== "new" && mayWrite && (
            <Button variant="outline" onClick={handleAddCommonContact}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add common contact
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <CommonContactTable
            commonContacts={commonContacts || []}
            orgId={orgId}
            mayDelete={mayDelete}
            isLoading={commonContactsQuery.isLoading}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex justify-between min-h-\[36px\]">
          <CardTitle>Service contacts</CardTitle>
          {searchParams.serviceContactId !== "new" && mayWrite && (
            <Button variant="outline" onClick={handleAddServiceContact}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add service contact
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <ServiceContactTable
            serviceContacts={serviceContacts || []}
            orgId={orgId}
            mayDelete={mayDelete}
            isLoading={serviceContactsQuery.isLoading}
          />
        </CardContent>
      </Card>
    </ResponsiveFormSection>
  );
}
